import React from 'react';
import { FederatedModule } from '@bfme-technology/spa';
import { getResolvedMfeProps } from '../Dashboard.utils';



import type { AssetAllocationProps } from './AssetAllocation.types';

export const AssetAllocation: React.FC<AssetAllocationProps> = ({ routesLoading = false, routes = [] }) => {
  const route = routes.find((r) => r.key === 'mfe-investing');

  if (routesLoading) {
    return (
      <section className="glass-panel p-5 md:p-6 flex flex-col gap-4 items-center justify-center min-h-[150px]">
        <div className="text-text-muted text-xs">Loading Asset Allocation MFE...</div>
      </section>
    );
  }

  if (!route) {
    return (
      <section className="glass-panel p-5 md:p-6 flex flex-col gap-4">
        <h3 className="text-sm md:text-base font-bold text-text-primary flex items-center gap-2">
          <i className="fa-solid fa-chart-pie text-primary-accent"></i>
          Asset Allocation
        </h3>
        <div className="flex flex-col sm:flex-row gap-5 items-center justify-between">
          {/* SVG circular breakdown ring */}
          <div className="relative shrink-0 w-28 h-28 flex items-center justify-center">
            <svg width="100%" height="100%" viewBox="0 0 36 36" className="overflow-visible">
              {/* Equities - 55% */}
              <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="var(--primary-accent)" strokeWidth="3" strokeDasharray="55 45" strokeDashoffset="25" />
              {/* Real Estate - 25% */}
              <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="var(--accent-violet)" strokeWidth="3" strokeDasharray="25 75" strokeDashoffset="-30" />
              {/* Crypto - 12% */}
              <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="var(--accent-magenta)" strokeWidth="3" strokeDasharray="12 88" strokeDashoffset="-55" />
              {/* Cash - 8% */}
              <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#fbbf24" strokeWidth="3" strokeDasharray="8 92" strokeDashoffset="-67" />
            </svg>
            <div className="absolute flex flex-col items-center justify-center">
              <span className="text-xs font-extrabold text-text-primary">$842.3k</span>
              <span className="text-[7px] text-text-muted uppercase tracking-wider font-extrabold">Total Value</span>
            </div>
          </div>

          {/* Legends with progress indicators */}
          <div className="flex-grow w-full flex flex-col gap-2.5">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-[10px] md:text-xs">
                <span className="font-bold text-text-primary flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary-accent inline-block"></span> Equities
                </span>
                <span className="text-text-secondary font-semibold">$463,265 (55%)</span>
              </div>
              <div className="h-1 w-full bg-bg-deep border border-border-color rounded-full overflow-hidden">
                <div className="h-full bg-primary-accent rounded-full" style={{ width: '55%' }}></div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-[10px] md:text-xs">
                <span className="font-bold text-text-primary flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-accent-violet inline-block"></span> Real Estate
                </span>
                <span className="text-text-secondary font-semibold">$210,575 (25%)</span>
              </div>
              <div className="h-1 w-full bg-bg-deep border border-border-color rounded-full overflow-hidden">
                <div className="h-full bg-accent-violet rounded-full" style={{ width: '25%' }}></div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-[10px] md:text-xs">
                <span className="font-bold text-text-primary flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-accent-magenta inline-block"></span> Cryptocurrencies
                </span>
                <span className="text-text-secondary font-semibold">$101,076 (12%)</span>
              </div>
              <div className="h-1 w-full bg-bg-deep border border-border-color rounded-full overflow-hidden">
                <div className="h-full bg-accent-magenta rounded-full" style={{ width: '12%' }}></div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-[10px] md:text-xs">
                <span className="font-bold text-text-primary flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span> Cash & Bonds
                </span>
                <span className="text-text-secondary font-semibold">$67,384 (8%)</span>
              </div>
              <div className="h-1 w-full bg-bg-deep border border-border-color rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: '8%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const resolved: any = getResolvedMfeProps(route);
  const { key, ...mfeProps } = resolved;
  return (
    <FederatedModule
      {...mfeProps}
      mfeType="federated"
      scope="mfe_investing"
      module="./AssetAllocation"
    />
  );
};
